import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Modal,
  Form,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';
import {
  SUPPORT_UNREAD_POLL_INTERVAL_MS,
  ADMIN_SUPPORT_CHAT_LIST_POLL_INTERVAL_MS,
  SUPPORT_UNREAD_FETCH_MIN_INTERVAL_MS,
} from '../../constants/polling';
import AdminStoreSearchCombobox from '../Admin/AdminStoreSearchCombobox';
import SupportChat from './SupportChat';
import { shouldShowSupportChatSubject } from './supportChatSubject';

// Custom styles for chat list items to override Bootstrap's default hover
// Using very specific selectors with high specificity to override Bootstrap's styles
const chatListItemStyle = `
  div.list-group.list-group-flush div.chat-list-item {
    background-color: transparent !important;
    color: var(--bs-body-color) !important;
  }
  div.list-group.list-group-flush div.chat-list-item:hover,
  div.list-group.list-group-flush div.chat-list-item:focus,
  div.list-group.list-group-flush div.chat-list-item:active,
  div.list-group.list-group-flush div.chat-list-item:hover:not(.active),
  div.list-group.list-group-flush div.chat-list-item:focus:not(.active),
  div.list-group.list-group-flush div.chat-list-item:active:not(.active) {
    background-color: var(--bs-secondary-bg) !important;
    color: var(--bs-body-color) !important;
    border-color: var(--bs-border-color) !important;
    z-index: 1;
  }
  div.list-group.list-group-flush div.chat-list-item:hover *,
  div.list-group.list-group-flush div.chat-list-item:focus *,
  div.list-group.list-group-flush div.chat-list-item:active *,
  div.list-group.list-group-flush div.chat-list-item:hover:not(.active) *,
  div.list-group.list-group-flush div.chat-list-item:focus:not(.active) *,
  div.list-group.list-group-flush div.chat-list-item:active:not(.active) * {
    color: var(--bs-body-color) !important;
  }
  div.list-group.list-group-flush div.chat-list-item.active {
    background-color: var(--bs-primary) !important;
    color: white !important;
    border-color: var(--bs-primary) !important;
  }
  div.list-group.list-group-flush div.chat-list-item.active * {
    color: white !important;
  }
  div.list-group.list-group-flush div.chat-list-item.active:hover,
  div.list-group.list-group-flush div.chat-list-item.active:focus,
  div.list-group.list-group-flush div.chat-list-item.active:active {
    background-color: var(--bs-primary) !important;
    color: white !important;
    border-color: var(--bs-primary) !important;
  }
  div.list-group.list-group-flush div.chat-list-item.active:hover *,
  div.list-group.list-group-flush div.chat-list-item.active:focus *,
  div.list-group.list-group-flush div.chat-list-item.active:active * {
    color: white !important;
  }
  /* Override any Bootstrap list-group-item-action styles */
  div.list-group.list-group-flush div.chat-list-item.list-group-item-action:hover {
    background-color: var(--bs-secondary-bg) !important;
    color: var(--bs-body-color) !important;
  }
  div.list-group.list-group-flush div.chat-list-item.list-group-item-action:hover * {
    color: var(--bs-body-color) !important;
  }
`;

const SupportChatList = ({
  isAdmin = false,
  onUnreadCountChange,
  onNotificationAdd,
  onSelectedChatChange,
  initialChat,
  onInitialChatConsumed,
  embeddedInChatApp = false,
}) => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [chats, setChats] = useState([]);
  const [filteredChats, setFilteredChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatSubject, setNewChatSubject] = useState('');
  const [creatingChat, setCreatingChat] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastAudience, setBroadcastAudience] = useState('all_clients');
  const [broadcastStoreId, setBroadcastStoreId] = useState('');
  const [broadcastUserIdsRaw, setBroadcastUserIdsRaw] = useState('');
  const [broadcastContent, setBroadcastContent] = useState('');
  const [broadcastTranslated, setBroadcastTranslated] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedAdminSearch, setDebouncedAdminSearch] = useState('');
  const [adminStoreId, setAdminStoreId] = useState('');
  const [debouncedAdminStoreId, setDebouncedAdminStoreId] = useState('');

  // Admin user search state
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [userSearchTimeout, setUserSearchTimeout] = useState(null);
  const userSearchRef = useRef(null);
  
  // Mobile view state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);
  
  // New message notification state
  const [notifications, setNotifications] = useState([]);
  const previousChatsRef = useRef([]);
  const notificationTimeoutRef = useRef({});
  
  // Check for mobile viewport
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use refs to avoid dependency issues
  const selectedChatIdRef = useRef(selectedChatId);
  const userIdRef = useRef(user?.id);
  const initialChatRef = useRef(null);
  
  // Keep refs updated
  useEffect(() => {
    selectedChatIdRef.current = selectedChatId;
  }, [selectedChatId]);
  
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  useEffect(() => {
    if (!isAdmin) {
      setDebouncedAdminSearch('');
      return;
    }
    const timer = setTimeout(() => setDebouncedAdminSearch(searchQuery.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchQuery, isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      setDebouncedAdminStoreId('');
      return;
    }
    const timer = setTimeout(() => setDebouncedAdminStoreId(adminStoreId.trim()), 350);
    return () => clearTimeout(timer);
  }, [adminStoreId, isAdmin]);

  // Load chats
  const loadChats = useCallback(async () => {
    try {
      setLoading(true);
      const listOptions = {};
      if (isAdmin) {
        if (debouncedAdminSearch) listOptions.q = debouncedAdminSearch;
        if (debouncedAdminStoreId) listOptions.store_id = debouncedAdminStoreId;
      }
      const response = await api.getSupportChats(listOptions);
      if (response.data) {
        // Notifications are disabled - no need to check for new messages
        previousChatsRef.current = response.data;
        const currentSelectedChatId = selectedChatIdRef.current;
        const serverChats = response.data;
        // If we have a pre-created chat (e.g. "Send receipt") not yet in server list, prepend it
        const initial = initialChatRef.current;
        const list = initial && !serverChats.some(c => c.id === initial.id)
          ? [initial, ...serverChats]
          : serverChats;
        setChats(list);
        if (currentSelectedChatId && onUnreadCountChangeRef.current && !embeddedInChatApp) {
          const serverChat = response.data.find(c => c.id === currentSelectedChatId);
          if (serverChat && serverChat.unread_count > 0) {
            onUnreadCountChangeRef.current(serverChat.unread_count);
          }
        }
        // Auto-select + recover from stale selection / ?chat= after deleted chats or partial DB restore
        const listForSelect = initialChatRef.current && !response.data.some(c => c.id === initialChatRef.current.id)
          ? [initialChatRef.current, ...response.data]
          : response.data;
        const idsInList = new Set(listForSelect.map((c) => c.id));

        const urlParams = new URLSearchParams(window.location.search);
        const rawChatParam = urlParams.get('chat');
        if (rawChatParam) {
          const cid = parseInt(rawChatParam, 10);
          if (!Number.isNaN(cid) && !idsInList.has(cid)) {
            urlParams.delete('chat');
            const newUrl =
              window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
            window.history.replaceState({}, document.title, newUrl);
          }
        }

        setSelectedChatId((prev) => {
          const urlAfterCleanup = new URLSearchParams(window.location.search);
          const raw = urlAfterCleanup.get('chat');
          const urlChatInt = raw ? parseInt(raw, 10) : NaN;
          const urlChatValid = !Number.isNaN(urlChatInt) && idsInList.has(urlChatInt);

          if (listForSelect.length === 0) {
            return null;
          }

          if (prev != null && !idsInList.has(prev)) {
            if (!isAdmin) {
              return listForSelect[0].id;
            }
            if (!isMobile) {
              return listForSelect[0].id;
            }
            return null;
          }

          if (prev != null) {
            return prev;
          }

          if (urlChatValid) {
            return urlChatInt;
          }

          const userSinglePrimary = !isAdmin;
          if (userSinglePrimary || (!isMobile && isAdmin)) {
            return listForSelect[0].id;
          }

          return prev;
        });
      }
    } catch (err) {
      setError(err.data?.errorText || t('support.failedToLoadChats'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isMobile, debouncedAdminSearch, debouncedAdminStoreId]);
  
  // Cleanup notification timeouts on unmount
  useEffect(() => {
    const timeouts = notificationTimeoutRef.current;
    return () => {
      Object.values(timeouts).forEach(timeout => clearTimeout(timeout));
    };
  }, []);
  
  // Handle notification click - go to chat
  const handleNotificationClick = (chatId) => {
    // Remove the notification
    setNotifications(prev => prev.filter(n => n.chatId !== chatId));
    // Clear timeout if exists
    Object.keys(notificationTimeoutRef.current).forEach(key => {
      if (key.startsWith(`${chatId}-`)) {
        clearTimeout(notificationTimeoutRef.current[key]);
        delete notificationTimeoutRef.current[key];
      }
    });
    // Navigate to chat
    setSelectedChatId(chatId);
    if (isMobile) {
      setShowChatOnMobile(true);
    }
  };
  
  // Dismiss notification
  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (notificationTimeoutRef.current[notificationId]) {
      clearTimeout(notificationTimeoutRef.current[notificationId]);
      delete notificationTimeoutRef.current[notificationId];
    }
  };

  // Initialize filtered chats when chats change
  useEffect(() => {
    setFilteredChats(chats);
  }, [chats]);

  // Filter chats based on search query (client-side for non-admin only; admin uses API ?q=)
  useEffect(() => {
    if (isAdmin) {
      setFilteredChats(chats);
      return;
    }
    if (!searchQuery.trim()) {
      setFilteredChats(chats);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = chats.filter(chat => {
      // Search in subject
      if (chat.subject && chat.subject.toLowerCase().includes(query)) {
        return true;
      }
      // Search in user name/email (for admin)
      if (isAdmin) {
        if (chat.user_name && chat.user_name.toLowerCase().includes(query)) {
          return true;
        }
        if (chat.user_email && chat.user_email.toLowerCase().includes(query)) {
          return true;
        }
      }
      // Search in last message
      if (chat.last_message && chat.last_message.content && chat.last_message.content.toLowerCase().includes(query)) {
        return true;
      }
      return false;
    });
    setFilteredChats(filtered);
  }, [chats, searchQuery, isAdmin]);

  // Keep onUnreadCountChange ref updated
  const onUnreadCountChangeRef = useRef(onUnreadCountChange);
  useEffect(() => {
    onUnreadCountChangeRef.current = onUnreadCountChange;
  }, [onUnreadCountChange]);

  // Use ref to track chats for loadUnreadCount
  const chatsRef = useRef(chats);
  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  const unreadFetchInFlightRef = useRef(false);
  const lastUnreadFetchAtRef = useRef(0);
  const unreadNotifyDebounceRef = useRef(null);
  const messagesReadDebounceRef = useRef(null);

  const notifyParentUnread = useCallback((displayCount, legacyOptions = {}) => {
    const cb = onUnreadCountChangeRef.current;
    if (!cb) return;
    if (embeddedInChatApp) {
      if (typeof displayCount === 'number' && !Number.isNaN(displayCount)) {
        cb(displayCount);
      }
      return;
    }
    const { subtractCount = 0, immediateUpdate = false } = legacyOptions;
    cb(subtractCount, immediateUpdate);
  }, [embeddedInChatApp]);

  // Load unread count
  const loadUnreadCount = useCallback(async (force = false) => {
    const now = Date.now();
    if (
      !force
      && (unreadFetchInFlightRef.current || now - lastUnreadFetchAtRef.current < SUPPORT_UNREAD_FETCH_MIN_INTERVAL_MS)
    ) {
      return;
    }
    unreadFetchInFlightRef.current = true;
    lastUnreadFetchAtRef.current = now;
    try {
      const response = await api.getSupportChatUnreadCount();
      if (response.data) {
        const serverCount = response.data.unread_count || 0;
        const currentSelectedChatId = selectedChatIdRef.current;
        let subtractCount = 0;
        if (currentSelectedChatId) {
          const selectedChat = chatsRef.current.find(c => c.id === currentSelectedChatId);
          if (selectedChat && selectedChat.unread_count > 0) {
            subtractCount = selectedChat.unread_count;
          }
        }
        const displayCount = Math.max(0, serverCount - subtractCount);
        setUnreadCount(displayCount);
        notifyParentUnread(displayCount, { subtractCount, immediateUpdate: false });
      }
    } catch (err) {
      console.error('Failed to load unread count:', err);
    } finally {
      unreadFetchInFlightRef.current = false;
    }
  }, [notifyParentUnread]);

  const refreshUnreadAfterMarkUnread = useCallback(async () => {
    await loadChats();
    setTimeout(() => {
      const cid = selectedChatIdRef.current;
      const row = previousChatsRef.current?.find((c) => c.id === cid);
      if (cid && onSelectedChatChange && row) {
        onSelectedChatChange(cid, row.unread_count || 0);
      }
      loadUnreadCount(true);
    }, 0);
  }, [loadChats, loadUnreadCount, onSelectedChatChange]);

  useEffect(() => {
    const runUnread = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      loadUnreadCount();
    };
    const runAdminChats = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (isAdmin) loadChats();
    };

    loadChats();
    runUnread();

    const unreadId = setInterval(runUnread, SUPPORT_UNREAD_POLL_INTERVAL_MS);
    const adminListId = isAdmin
      ? setInterval(runAdminChats, ADMIN_SUPPORT_CHAT_LIST_POLL_INTERVAL_MS)
      : null;

    const onVis = () => {
      if (document.hidden) return;
      runUnread();
      if (isAdmin) loadChats();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      clearInterval(unreadId);
      if (adminListId) clearInterval(adminListId);
      document.removeEventListener('visibilitychange', onVis);
      clearTimeout(unreadNotifyDebounceRef.current);
      clearTimeout(messagesReadDebounceRef.current);
    };
  }, [loadChats, loadUnreadCount, isAdmin]);

  // When opened with a pre-created chat (e.g. "Send receipt"), add it to the list and open immediately
  useEffect(() => {
    if (!initialChat || isAdmin) return;
    initialChatRef.current = initialChat;
    setChats(prev => (prev.some(c => c.id === initialChat.id) ? prev : [initialChat, ...prev]));
    setSelectedChatId(initialChat.id);
    if (isMobile) setShowChatOnMobile(true);
    onInitialChatConsumed?.();
    // Clear ref after a tick so in-flight loadChats can merge; next poll won't re-merge
    const t = setTimeout(() => { initialChatRef.current = null; }, 0);
    return () => clearTimeout(t);
  }, [initialChat, isAdmin, isMobile]);

  // Handle initial chat ID or user_id from URL parameter
  useEffect(() => {
    // Check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const chatIdFromUrl = urlParams.get('chat');
    const userIdFromUrl = urlParams.get('user_id');
    
    // Handle user_id parameter (for admin to start chat with user)
    if (userIdFromUrl && isAdmin) {
      const userIdInt = parseInt(userIdFromUrl, 10);
      if (!isNaN(userIdInt)) {
        // Fetch user info and open new chat modal
        const fetchUserAndOpenModal = async () => {
          try {
            // Search for user by ID
            const response = await api.searchUsers(userIdInt.toString(), 1);
            if (response.data && response.data.length > 0) {
              const user = response.data[0];
              setSelectedUser(user);
              setUserSearchQuery(user.full_name || user.email);
              setShowNewChatModal(true);
            } else {
              // User not found, but still open modal
              setShowNewChatModal(true);
            }
            // Clear URL parameter
            urlParams.delete('user_id');
            const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
            window.history.replaceState({}, document.title, newUrl);
          } catch (err) {
            console.error('Failed to fetch user:', err);
            // Still open modal even if fetch fails
            setShowNewChatModal(true);
            // Clear URL parameter
            urlParams.delete('user_id');
            const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
            window.history.replaceState({}, document.title, newUrl);
          }
        };
        fetchUserAndOpenModal();
      }
    }
    
    if (chatIdFromUrl) {
      // Parse as integer
      const chatIdInt = parseInt(chatIdFromUrl, 10);
      if (!isNaN(chatIdInt)) {
        // Wait for chats to load, then select the chat
        const checkAndSelect = () => {
          if (chats.length > 0) {
            const chatExists = chats.find(c => c.id === chatIdInt);
            if (chatExists) {
              // Only set if not already selected (to avoid unnecessary updates)
              if (selectedChatId !== chatIdInt) {
                setSelectedChatId(chatIdInt);
                if (isMobile) {
                  setShowChatOnMobile(true);
                }
                // Notify parent component
                if (onSelectedChatChange) {
                  onSelectedChatChange(chatIdInt, chatExists.unread_count || 0);
                }
              }
              
              // Clear URL parameter after successfully opening chat
              urlParams.delete('chat');
              const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
              window.history.replaceState({}, document.title, newUrl);
            }
          }
        };
        
        // If chats are already loaded, select immediately
        if (chats.length > 0) {
          checkAndSelect();
        } else {
          // Otherwise wait a bit for chats to load, with retries
          let retries = 0;
          const maxRetries = 10; // Try for up to 5 seconds (10 * 500ms)
          const trySelect = () => {
            if (chats.length > 0) {
              checkAndSelect();
            } else if (retries < maxRetries) {
              retries++;
              setTimeout(trySelect, 500);
            }
          };
          const timeout = setTimeout(trySelect, 500);
          return () => clearTimeout(timeout);
        }
      }
    }
  }, [chats, selectedChatId, isMobile, onSelectedChatChange, isAdmin]);

  // Handle chat selection (for mobile)
  const handleSelectChat = (chatId) => {
    setSelectedChatId(chatId);
    if (isMobile) {
      setShowChatOnMobile(true);
    }
    // When a chat is selected, immediately update the unread count in Dashboard
    // by subtracting the unread count of the selected chat
    const selectedChat = chats.find(c => c.id === chatId);
    const unreadCount = selectedChat?.unread_count || 0;
    if (onSelectedChatChange) {
      onSelectedChatChange(chatId, unreadCount);
    }
    if (unreadCount > 0 && !embeddedInChatApp && onUnreadCountChangeRef.current) {
      onUnreadCountChangeRef.current(unreadCount, false);
    }
    clearTimeout(unreadNotifyDebounceRef.current);
    unreadNotifyDebounceRef.current = setTimeout(() => loadUnreadCount(true), 300);
  };
  
  // Handle back button on mobile
  const handleBackToList = () => {
    setShowChatOnMobile(false);
  };
  
  // Stable callback for messages read - use refs to avoid recreating
  const handleMessagesRead = useCallback(() => {
    const currentSelectedChatId = selectedChatIdRef.current;
    let unreadCountToSubtract = 0;
    if (currentSelectedChatId) {
      setChats(prev => {
        const updatedChats = prev.map(chat => {
          if (chat.id === currentSelectedChatId) {
            unreadCountToSubtract = chat.unread_count || 0;
            return { ...chat, unread_count: 0 };
          }
          return chat;
        });
        return updatedChats;
      });
      if (unreadCountToSubtract > 0) {
        setUnreadCount((prevCount) => {
          const next = Math.max(0, prevCount - unreadCountToSubtract);
          if (embeddedInChatApp) {
            notifyParentUnread(next);
          }
          return next;
        });
      }
    }
    if (onSelectedChatChange && currentSelectedChatId) {
      onSelectedChatChange(currentSelectedChatId, 0);
    }
    if (!embeddedInChatApp && onUnreadCountChangeRef.current && unreadCountToSubtract > 0) {
      onUnreadCountChangeRef.current(unreadCountToSubtract, true);
    }
    clearTimeout(messagesReadDebounceRef.current);
    messagesReadDebounceRef.current = setTimeout(() => {
      loadUnreadCount(true);
    }, 600);
  }, [embeddedInChatApp, loadUnreadCount, notifyParentUnread, onSelectedChatChange]);

  // Search users (admin only)
  useEffect(() => {
    if (!isAdmin || !userSearchQuery.trim()) {
      setUserSearchResults([]);
      return;
    }

    // Clear previous timeout
    if (userSearchTimeout) {
      clearTimeout(userSearchTimeout);
    }

    // Debounce search
    const timeout = setTimeout(async () => {
      try {
        setSearchingUsers(true);
        const response = await api.searchUsers(userSearchQuery.trim(), 20, debouncedAdminStoreId);
        if (response.data) {
          setUserSearchResults(response.data);
        }
      } catch (err) {
        console.error('Failed to search users:', err);
        setUserSearchResults([]);
      } finally {
        setSearchingUsers(false);
      }
    }, 300);

    setUserSearchTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [userSearchQuery, isAdmin, debouncedAdminStoreId]);

  // Reset modal state when closing
  const handleCloseNewChatModal = () => {
    setShowNewChatModal(false);
    setNewChatSubject('');
    setUserSearchQuery('');
    setUserSearchResults([]);
    setSelectedUser(null);
  };

  const handleCloseBroadcastModal = () => {
    if (broadcastSending) return;
    setShowBroadcastModal(false);
    setBroadcastResult(null);
  };

  const handleBroadcastSubmit = async (e) => {
    e.preventDefault();
    if (!user?.is_superuser) {
      setError(t('support.broadcastSuperuserOnly'));
      return;
    }
    const text = broadcastContent.trim();
    if (!text) return;
    if (!window.confirm(t('support.broadcastConfirm'))) return;

    const payload = {
      content: text,
      audience: broadcastAudience,
    };
    if (broadcastTranslated.trim()) {
      payload.translated_content = broadcastTranslated.trim();
    }
    if (broadcastAudience === 'store') {
      const sid = broadcastStoreId.trim();
      if (!sid) {
        setError(t('support.broadcastNeedStoreId'));
        return;
      }
      payload.store_id = sid;
    }
    if (broadcastAudience === 'users') {
      const ids = broadcastUserIdsRaw
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => parseInt(s, 10))
        .filter((n) => !Number.isNaN(n));
      if (ids.length === 0) {
        setError(t('support.broadcastNeedUserIds'));
        return;
      }
      payload.user_ids = ids;
    }

    setBroadcastSending(true);
    setBroadcastResult(null);
    setError('');
    try {
      const res = await api.postSupportAdminBroadcast(payload);
      if (res.error) {
        setError(res.errorText || t('support.broadcastError'));
        return;
      }
      const d = res.data || {};
      setBroadcastResult({
        sent: d.sent ?? 0,
        failed: d.failed ?? 0,
      });
      setBroadcastContent('');
      setBroadcastTranslated('');
      loadChats();
      loadUnreadCount();
    } catch (err) {
      setError(err.data?.errorText || t('support.broadcastError'));
    } finally {
      setBroadcastSending(false);
    }
  };

  // Close user search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userSearchRef.current && !userSearchRef.current.contains(event.target)) {
        setUserSearchResults([]);
      }
    };

    if (showNewChatModal && isAdmin) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showNewChatModal, isAdmin]);

  // Create new chat
  const handleCreateChat = async (e) => {
    e.preventDefault();
    if (!newChatSubject.trim()) return;
    if (isAdmin && !selectedUser) {
      setError(t('support.selectUserFirst') || 'Пожалуйста, выберите пользователя');
      return;
    }

    try {
      setCreatingChat(true);
      const chatData = { subject: newChatSubject.trim() };
      if (isAdmin && selectedUser) {
        chatData.user_id = selectedUser.id;
      }
      const response = await api.createSupportChat(chatData);
      if (response.data) {
        setChats(prev => (prev.some(c => c.id === response.data.id) ? prev : [response.data, ...prev]));
        setSelectedChatId(response.data.id);
        handleCloseNewChatModal();
        if (isMobile) {
          setShowChatOnMobile(true);
        }
      }
    } catch (err) {
      setError(err.data?.errorText || t('support.failedToCreateChat'));
    } finally {
      setCreatingChat(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return t('support.justNow');
    if (minutes < 60) return t('support.minutesAgo', { minutes });
    // Use locale based on current language
    const localeMap = { en: 'en-US', ru: 'ru-RU', zh: 'zh-CN', tr: 'tr-TR' };
    const locale = localeMap[language] || 'en-US';
    if (diff < 86400000) return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString(locale);
  };

  const selectedChat = chats.find(c => c.id === selectedChatId);
  /** Non-admin: list API returns SupportChatDetailSerializer (includes messages) so SupportChat can skip GET …/chats/<id>/. */
  const prefetchedChatDetailForUser = useMemo(() => {
    if (isAdmin || !selectedChatId) return null;
    const row = chats.find((c) => c.id === selectedChatId);
    if (row && Array.isArray(row.messages)) return row;
    return null;
  }, [isAdmin, selectedChatId, chats]);

  const displayedChats = searchQuery ? filteredChats : chats;

  // Should show chat list on mobile?
  const showListOnMobile = !showChatOnMobile || !selectedChatId;
  const chatVisibleMobile =
    Boolean(selectedChatId) &&
    (showChatOnMobile || !isAdmin);
  
  return (
    <div
      className={`d-flex flex-column flex-md-row${embeddedInChatApp ? ' chat-app-support-list-embed' : ''}`}
      style={{
        height: embeddedInChatApp ? '100%' : 'calc(100vh - 200px)',
        minHeight: embeddedInChatApp ? 0 : '500px',
        flex: embeddedInChatApp ? 1 : undefined,
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: chatListItemStyle }} />
      
      {/* Notifications Toast Container - Only for regular users, not admins */}
      {!isAdmin && (
        <ToastContainer 
          position="bottom-end" 
          className="p-3" 
          style={{ zIndex: 1050, position: 'fixed', bottom: 0, right: 0 }}
        >
          {notifications.map((notification) => (
            <Toast 
              key={notification.id}
              show={true}
              onClose={() => dismissNotification(notification.id)}
              className="mb-2 new-message-toast"
              style={{ 
                minWidth: '320px',
                maxWidth: '400px',
                backgroundColor: 'var(--card-bg, #ffffff)',
                border: '2px solid var(--bs-primary)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                opacity: 1,
              }}
              bg="light"
            >
              <Toast.Header 
                className="text-white"
                style={{ 
                  backgroundColor: 'var(--bs-primary)',
                  borderBottom: 'none',
                }}
              >
                <i className="bi bi-chat-dots me-2"></i>
                <strong className="me-auto">{notification.senderName}</strong>
                <small style={{ color: 'rgba(255,255,255,0.8)' }}>{t('support.justNow')}</small>
              </Toast.Header>
              <Toast.Body 
                style={{ 
                  cursor: 'pointer',
                  backgroundColor: 'var(--card-bg, #ffffff)',
                  color: 'var(--bs-body-color)',
                  padding: '1rem',
                }}
                onClick={() => handleNotificationClick(notification.chatId)}
              >
                <div className="small mb-1" style={{ color: 'var(--bs-secondary-color)' }}>
                  {notification.chatSubject}
                </div>
                <div className="fw-medium" style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  color: 'var(--bs-body-color)',
                }}>
                  {notification.message}
                </div>
              <div className="mt-2">
                <Button 
                  size="sm" 
                  variant="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNotificationClick(notification.chatId);
                  }}
                >
                  <i className="bi bi-arrow-right-circle me-1"></i>
                  {t('support.goToChat')}
                </Button>
              </div>
            </Toast.Body>
          </Toast>
          ))}
        </ToastContainer>
      )}
      
      {/* Chat List Sidebar (admin only) */}
      {isAdmin && (
      <Card 
        className={`d-flex flex-column ${isMobile ? (showListOnMobile ? 'w-100' : 'd-none') : 'me-2'}`} 
        style={isMobile ? {} : { width: '300px', minWidth: '250px', maxWidth: '350px' }}
      >
        <Card.Header className="flex-shrink-0">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0" style={{ color: 'var(--bs-body-color)' }}>
              {t('support.primaryTab')}
            </h5>
            <div className="d-flex align-items-center gap-2">
              {user?.is_superuser && (
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={() => {
                    setBroadcastResult(null);
                    setShowBroadcastModal(true);
                  }}
                  title={t('support.broadcastOpen')}
                >
                  <i className="bi bi-megaphone"></i>
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowNewChatModal(true)}
                title={t('support.newChat')}
              >
                <i className="bi bi-plus-lg"></i>
              </Button>
              {unreadCount > 0 && (
                <Badge bg="danger" pill>{unreadCount}</Badge>
              )}
            </div>
          </div>
          {user?.is_superuser ? (
            <AdminStoreSearchCombobox
              className="mt-1"
              size="sm"
              filterValue={adminStoreId}
              onFilterValueChange={setAdminStoreId}
              valueField="store_id"
              placeholder={t('support.filterByStoreId')}
              loadingText={t('wbAdminReturns.storeSearching')}
            />
          ) : (
            <Form.Control
              type="text"
              className="mt-1"
              size="sm"
              placeholder={t('support.filterByStoreId')}
              value={adminStoreId}
              onChange={(e) => setAdminStoreId(e.target.value)}
            />
          )}
        </Card.Header>
        <Card.Body className="p-0 d-flex flex-column" style={{ minHeight: 0 }}>
          {/* Search input */}
          <div className="p-2 border-bottom flex-shrink-0">
            <Form.Control
              type="text"
              placeholder={isAdmin ? t('support.searchChats') : t('support.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="sm"
            />
          </div>
          {loading && chats.length === 0 ? (
            <div className="text-center p-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : displayedChats.length === 0 ? (
            <div className="text-center p-3" style={{ color: 'var(--bs-secondary-color)' }}>
              {searchQuery ? t('support.nothingFound') : t('support.noChats')}
            </div>
          ) : (
            <div className="list-group list-group-flush flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
              {displayedChats.map((chat) => (
                <div
                  key={chat.id}
                  onClick={() => handleSelectChat(chat.id)}
                  className={`d-flex justify-content-between align-items-start chat-list-item ${chat.id === selectedChatId ? 'active' : ''}`}
                  style={{
                    backgroundColor: chat.id === selectedChatId ? 'var(--bs-primary)' : 'transparent',
                    color: chat.id === selectedChatId ? 'white' : 'var(--bs-body-color)',
                    cursor: 'pointer',
                    border: 'none',
                    borderBottom: '1px solid var(--bs-border-color)',
                    padding: '0.75rem 1rem',
                    transition: 'background-color 0.15s ease-in-out',
                  }}
                >
                  <div className="flex-grow-1 overflow-hidden me-2">
                    <div className="fw-semibold text-truncate" style={{ color: chat.id === selectedChatId ? 'rgba(255,255,255,0.95)' : 'var(--bs-body-color)' }}>
                      {chat.user_email}
                    </div>
                    {chat.user_name && (
                      <div className="small text-truncate" style={{ color: chat.id === selectedChatId ? 'rgba(255,255,255,0.9)' : 'var(--bs-secondary-color)' }}>
                        {chat.user_name}
                      </div>
                    )}
                    {shouldShowSupportChatSubject(chat.subject) && (
                      <div className="small mt-1" style={{ color: chat.id === selectedChatId ? 'white' : 'var(--bs-body-color)' }}>
                        <div className="text-truncate">{chat.subject}</div>
                        {isAdmin && chat.subject_translated && chat.subject && chat.subject !== chat.subject_translated && (
                          <div className="text-truncate mt-1" style={{ fontStyle: 'italic', opacity: 0.8, fontSize: '0.85em' }}>
                            <i className="bi bi-translate me-1"></i>
                            {chat.subject_translated}
                          </div>
                        )}
                      </div>
                    )}
                    {chat.last_message && (
                      <div className="small text-truncate mt-1" style={{ color: chat.id === selectedChatId ? 'rgba(255,255,255,0.9)' : 'var(--bs-body-color)', opacity: chat.id === selectedChatId ? 0.9 : 0.85 }}>
                        {chat.last_message.attachment_type && (
                          <i className="bi bi-paperclip me-1"></i>
                        )}
                        {chat.last_message.content}
                      </div>
                    )}
                    <div className="small mt-1" style={{ color: chat.id === selectedChatId ? 'rgba(255,255,255,0.75)' : 'var(--bs-secondary-color)' }}>
                      {formatDate(chat.last_message_at || chat.created_at)}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    {chat.unread_count > 0 && (
                      <Badge bg="danger" pill>{chat.unread_count}</Badge>
                    )}
                    <Badge bg={chat.status === 'open' ? 'success' : 'secondary'} className="d-none d-sm-inline">
                      {chat.status === 'open' ? t('support.open') : t('support.closed')}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
      )}

      {/* Chat View */}
      <div
        className={`flex-grow-1 d-flex flex-column${
          embeddedInChatApp ? ' chat-app-support-chat-pane' : ''
        } ${isMobile ? (chatVisibleMobile ? 'w-100' : 'd-none') : ''}`}
        style={{ minWidth: 0 }}
      >
        {!isAdmin && !embeddedInChatApp && (
          <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
            <h5 className="mb-0" style={{ color: 'var(--bs-body-color)' }}>
              {t('support.myChats')}
            </h5>
            {unreadCount > 0 && (
              <Badge bg="danger" pill>{unreadCount}</Badge>
            )}
          </div>
        )}
        {selectedChatId ? (
          <SupportChat
            key={selectedChatId}
            chatId={selectedChatId}
            isAdmin={isAdmin}
            embeddedInChatApp={embeddedInChatApp}
            prefetchedChatDetail={prefetchedChatDetailForUser}
            onChatMissing={() => {
              setSelectedChatId(null);
              loadChats();
            }}
            onUnreadTotalsRefresh={refreshUnreadAfterMarkUnread}
            onClose={() => {
              if (isMobile) {
                handleBackToList();
              } else {
                setSelectedChatId(null);
              }
            }}
            onMessagesRead={handleMessagesRead}
            showBackButton={isMobile && isAdmin}
          />
        ) : (
          <Card className="h-100">
            <Card.Body className="d-flex justify-content-center align-items-center" style={{ color: 'var(--bs-secondary-color)' }}>
              {isMobile ? t('support.noSelectedChat') : t('support.selectChatToView')}
            </Card.Body>
          </Card>
        )}
      </div>

      {/* New Chat Modal */}
      <Modal show={showNewChatModal} onHide={handleCloseNewChatModal} size={isAdmin ? 'lg' : undefined}>
        <Modal.Header closeButton>
          <Modal.Title>{t('support.newSupportChat')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateChat}>
          <Modal.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {isAdmin && (
              <Form.Group className="mb-3">
                <Form.Label>{t('support.selectUser') || 'Выберите пользователя'}</Form.Label>
                <div ref={userSearchRef} style={{ position: 'relative' }}>
                  <Form.Control
                    type="text"
                    placeholder={t('support.searchUser') || 'Поиск по email или имени...'}
                    value={userSearchQuery}
                    onChange={(e) => {
                      setUserSearchQuery(e.target.value);
                      setSelectedUser(null);
                    }}
                    autoFocus
                  />
                  {userSearchQuery && !selectedUser && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        backgroundColor: 'var(--bs-body-bg)',
                        border: '1px solid var(--bs-border-color)',
                        borderRadius: '0.375rem',
                        marginTop: '0.25rem',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                      }}
                    >
                      {searchingUsers ? (
                        <div className="text-center p-3">
                          <Spinner animation="border" size="sm" />
                        </div>
                      ) : userSearchResults.length > 0 ? (
                        userSearchResults.map((user) => (
                          <div
                            key={user.id}
                            onClick={() => {
                              setSelectedUser(user);
                              setUserSearchQuery(user.full_name || user.email);
                              setUserSearchResults([]);
                            }}
                            style={{
                              padding: '0.75rem 1rem',
                              cursor: 'pointer',
                              borderBottom: '1px solid var(--bs-border-color)',
                              color: 'var(--bs-body-color)',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--bs-secondary-bg)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <div className="fw-bold">{user.full_name}</div>
                            <div className="small text-muted">{user.email}</div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center p-3 text-muted">
                          {t('support.noUsersFound') || 'Пользователи не найдены'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {selectedUser && (
                  <div className="mt-2 p-2 bg-light rounded" style={{ backgroundColor: 'var(--bs-secondary-bg)' }}>
                    <div className="small text-muted">{t('support.selectedUser') || 'Выбранный пользователь'}:</div>
                    <div className="fw-bold">{selectedUser.full_name}</div>
                    <div className="small text-muted">{selectedUser.email}</div>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 mt-1"
                      onClick={() => {
                        setSelectedUser(null);
                        setUserSearchQuery('');
                      }}
                    >
                      {t('support.change') || 'Изменить'}
                    </Button>
                  </div>
                )}
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>{t('support.subject')}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t('support.describeProblem')}
                value={newChatSubject}
                onChange={(e) => setNewChatSubject(e.target.value)}
                autoFocus={!isAdmin}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseNewChatModal}>
              {t('support.cancel')}
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={!newChatSubject.trim() || creatingChat || (isAdmin && !selectedUser)}
            >
              {creatingChat ? <Spinner animation="border" size="sm" /> : t('support.create')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showBroadcastModal} onHide={handleCloseBroadcastModal} size="lg" backdrop={broadcastSending ? 'static' : true}>
        <Modal.Header closeButton>
          <Modal.Title>{t('support.broadcastTitle')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleBroadcastSubmit}>
          <Modal.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {broadcastResult && (
              <Alert variant="success">
                {t('support.broadcastDone', {
                  sent: String(broadcastResult.sent),
                  failed: String(broadcastResult.failed),
                })}
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>{t('support.broadcastAudience')}</Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  id="bc-all"
                  name="bc_aud"
                  label={t('support.broadcastAudienceAll')}
                  checked={broadcastAudience === 'all_clients'}
                  onChange={() => setBroadcastAudience('all_clients')}
                />
                <Form.Check
                  type="radio"
                  id="bc-store"
                  name="bc_aud"
                  label={t('support.broadcastAudienceStore')}
                  checked={broadcastAudience === 'store'}
                  onChange={() => setBroadcastAudience('store')}
                />
                <Form.Check
                  type="radio"
                  id="bc-users"
                  name="bc_aud"
                  label={t('support.broadcastAudienceUsers')}
                  checked={broadcastAudience === 'users'}
                  onChange={() => setBroadcastAudience('users')}
                />
              </div>
            </Form.Group>
            {broadcastAudience === 'store' && (
              <Form.Group className="mb-3">
                <Form.Label>{t('support.filterByStoreId')}</Form.Label>
                <Form.Control
                  size="sm"
                  value={broadcastStoreId}
                  onChange={(e) => setBroadcastStoreId(e.target.value)}
                  placeholder={t('support.broadcastStoreIdHint')}
                />
              </Form.Group>
            )}
            {broadcastAudience === 'users' && (
              <Form.Group className="mb-3">
                <Form.Label>{t('support.broadcastUserIdsLabel')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  value={broadcastUserIdsRaw}
                  onChange={(e) => setBroadcastUserIdsRaw(e.target.value)}
                  placeholder={t('support.broadcastUserIdsHint')}
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>{t('support.broadcastMessageLabel')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={broadcastContent}
                onChange={(e) => setBroadcastContent(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>{t('support.broadcastTranslatedLabel')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={broadcastTranslated}
                onChange={(e) => setBroadcastTranslated(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseBroadcastModal} disabled={broadcastSending}>
              {t('support.cancel')}
            </Button>
            <Button variant="warning" type="submit" disabled={broadcastSending || !broadcastContent.trim()}>
              {broadcastSending ? <Spinner animation="border" size="sm" /> : t('support.broadcastSend')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default SupportChatList;
